import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../PageHeader/PageHeader';
import { HitApi } from '../../Api/ApiHit';
import { deleteSeat, searchSeat } from '../../constant/Constant';
import { useDispatch, useSelector } from 'react-redux';
import { setApiJson } from '../../redux/Actions/ApiAction';
import AppTable from '../../components/AppTable/AppTable';
import { setPagination } from '../../redux/Actions/TableAction';
import DeleteButton from '../../components/Delete/DeleteButton';
import EditButton from '../../components/Edit/EditButton';
import toast from 'react-hot-toast';
import TableSearch from '../../components/TableSearch/TableSearch';
import { hasPermission } from '../../utils/permissionUtils';
import { getSelectedUnit } from '../../storage/Storage';

function SeatMaster() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const ApiReducer = useSelector((state) => state.ApiReducer);
    const TableDataReducer = useSelector((state) => state.TableReducer);
    const getUnit = getSelectedUnit();

    const [optionSelect, setOptionSelect] = useState('');
    const [valueSearched, setValueSearched] = useState('');
    const [isSearching, setIsSearching] = useState(false);

    // Permissions
    const canEditSeat = hasPermission('seat', 'write');
    const canReadSeat = hasPermission('seat', 'read');
    const canDeleteSeat = hasPermission('seat', 'delete');
    const canAddSeat = hasPermission('seat', 'write');

    useEffect(() => {
        if (canReadSeat) {
            loadSeat();
        }
    }, [TableDataReducer.pagination.page, TableDataReducer.pagination.limit, canReadSeat]);

    const loadSeat = (searchKey, searchValue) => {
        if (!canReadSeat) {
            toast.error('You do not have permission to view seats');
            return;
        }

        setIsSearching(true);

        const searchObj = { unitIds: getUnit?._id };

        if (searchKey && searchValue) {
            searchObj[searchKey] = searchValue;
        }

        const json = {
            page: TableDataReducer.pagination.page,
            limit: TableDataReducer.pagination.limit,
            search: searchObj
        };

        HitApi(json, searchSeat).then((res) => {
            const seatData = Array.isArray(res?.data) ? res?.data : [];

            if (res?.message === "Seats retrieved successfully" || seatData.length > 0) {
                dispatch(setApiJson(seatData));
                dispatch(setPagination({
                    total:res?.data?.totalDocs || 0,
                    page: TableDataReducer.pagination.page,
                    limit: TableDataReducer.pagination.limit
                }));
            } else {
                dispatch(setApiJson([]));
                toast.error(res?.message || 'Failed to fetch seats.');
            }
        }).catch(() => {
            dispatch(setApiJson([]));
            toast.error('Error fetching seats. Please try again.');
        }).finally(() => {
            setIsSearching(false);
        });
    };

    const handleSearch = () => {
        if (!canReadSeat) {
            toast.error('You do not have permission to search seats');
            return;
        }

        if (!optionSelect) {
            toast.error('Please select a search option');
            return;
        }

        if (!valueSearched.trim()) {
            toast.error('Please enter a search value');
            return;
        }

        loadSeat(optionSelect, valueSearched.trim());
    };

    const handleClearSearch = () => {
        if (!canReadSeat) {
            toast.error('You do not have permission to access seats');
            return;
        }

        setOptionSelect('');
        setValueSearched('');
        loadSeat();
    };

    const handleEdit = (row) => {
        if (!canEditSeat) {
            toast.error('You do not have permission to edit seats');
            return;
        }
        navigate(`/seat/edit/${row._id}`);
    };

    const handleDelete = (row) => {
        if (!canDeleteSeat) {
            toast.error('You do not have permission to delete seats');
            return;
        }

        if (window.confirm(`Are you sure you want to delete ${row.seatName || 'this seat'}?`)) {
            HitApi({ _id: row?._id }, deleteSeat).then((res) => {
                if (res?.success || res?.message === "Seat deleted successfully") {
                    toast.success('Seat deleted successfully!');
                    loadSeat();
                } else {
                    toast.error(res?.message || 'Failed to delete seat.');
                }
            }).catch(() => {
                toast.error('Error occurred while deleting seat.');
            });
        }
    };

    const renderSeatStatus = (status) => {
        const normalized = status?.toString().toLowerCase();
        const statusMap = {
            available: ['bg-green-100', 'text-green-800', 'Available'],
            free: ['bg-green-100', 'text-green-800', 'Available'],
            occupied: ['bg-red-100', 'text-red-800', 'Occupied'],
            booked: ['bg-red-100', 'text-red-800', 'Occupied'],
            maintenance: ['bg-yellow-100', 'text-yellow-800', 'Maintenance'],
            'under maintenance': ['bg-yellow-100', 'text-yellow-800', 'Maintenance'],
            reserved: ['bg-blue-100', 'text-blue-800', 'Reserved']
        };

        const [bg, text, label] = statusMap[normalized] || ['bg-gray-100', 'text-gray-800', status || 'N/A'];
        return <span className={`px-2 py-1 rounded-full text-xs font-medium ${bg} ${text}`}>{label}</span>;
    };

    const baseIndex = (TableDataReducer.pagination.page - 1) * TableDataReducer.pagination.limit;
    const baseTableHeaders = [
        {
            title: "S.No",
            key: "serialNumber",
            width: "60px",
            align: "center",
            render: (_, __, index) => (
                <div className="text-center">
                    <span className="text-sm font-medium text-gray-900">{baseIndex + index + 1}</span>
                </div>
            )
        },
        {
            title: "Seat Code",
            key: "seatCode",
            render: (value) => (
                <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">
                    {value || 'N/A'}
                </span>
            )
        },
        {
            title: "Seat Name",
            key: "seatName",
            render: (value) => <span className="font-medium text-gray-900">{value || 'N/A'}</span>
        },
        {
            title: "Seat For",
            key: "seatFor",
            render: (value) => <span className="capitalize text-sm">{value || 'N/A'}</span>
        },
        {
            title: "Seat Status",
            key: "status",
            render: renderSeatStatus
        },
        {
            title: "Actions",
            key: "actions",
            align: "center",
            render: (_, row) => {
                if (!canEditSeat && !canDeleteSeat) {
                    return <span className="text-sm text-gray-400">No actions available</span>;
                }

                return (
                    <div className="flex justify-center space-x-2">
                        {canEditSeat && (
                            <EditButton
                                onEdit={() => handleEdit(row)}
                                itemName="seat"
                            />
                        )}
                        {canDeleteSeat && (
                            <DeleteButton
                                onDelete={() => handleDelete(row)}
                                itemName="seat"
                            />
                        )}
                    </div>
                );
            }
        }
    ];

    const searchOptions = [
        { label: 'Seat Code', value: 'seatCode' },
        { label: 'Seat Name', value: 'seatName' },
        { label: 'Seat For', value: 'seatFor' },
        { label: 'Seat Status', value: 'status' },
    ];

    if (!canReadSeat) {
        return (
            <div className="p-5">
                <PageHeader
                    title={'Seat Management'}
                    description={'Manage and view seat arrangements'}
                />
                <div className="flex justify-center items-center h-64">
                    <div className="text-center">
                        <h3 className="text-lg font-medium text-red-600 mb-2">Access Denied</h3>
                        <p className="text-gray-600">You don't have permission to view seats.</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="p-5">
            <PageHeader
                title={'Seat Management'}
                description={'Manage and view seat arrangements'}
            />

            <AppTable
                TH={baseTableHeaders}
                TD={Array.isArray(ApiReducer.apiJson) ? ApiReducer.apiJson : []}
                isLoading={isSearching}
                emptyMessage="No seats found"
                buttonText={canAddSeat ? "Add Seat" : undefined}
                navigateTo={canAddSeat ? "/seat/add" : undefined}
                TableSearch={
                    <TableSearch
                        options={searchOptions}
                        onClick={handleSearch}
                        optionSelect={optionSelect}
                        setOptionSelect={setOptionSelect}
                        setValueSearched={setValueSearched}
                        valueSearched={valueSearched}
                        onClear={handleClearSearch}
                    />
                }
            />
        </div>
    );
}

export default SeatMaster;
